/**
 * GovAudit — Cross-tenant audit super-view for the Government /
 * system_admin persona. Regulators (QFCRA, MoCI, QFMA) can audit
 * the entire platform from one screen — every action, every tenant,
 * every actor.
 *
 *   • All personal emails are SHA-256-hashed before display.
 *   • Free-text resource labels run through the universal PII
 *     redactor before they ever leave the backend.
 *   • Every read of this page is itself written into audit_log.
 *
 * Layout:
 *   1. Summary header: total events + top 5 tenants + top 5 actions.
 *   2. Live filter bar: days, action, tenant.
 *   3. Paginated event table.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, RefreshCw, Filter, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

function fmtAt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function exportCsv(rows) {
  if (rows.length === 0) {
    queueMicrotask(() => toast.info('Nothing to export.'));
    return;
  }
  const head = ['at', 'tenant_name', 'tenant_id', 'actor', 'actor_role', 'action', 'resource_type', 'resource_label'];
  const csv = [head.join(',')].concat(
    rows.map((r) => head.map((k) => JSON.stringify(r[k] ?? '')).join(',')),
  ).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `cross-tenant-audit-${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default function GovAudit() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [action, setAction] = useState('');
  const [tenant, setTenant] = useState('');

  const load = async () => {
    queueMicrotask(() => setLoading(true));
    try {
      const q = new URLSearchParams({ days: String(days), limit: '200' });
      if (action) q.append('action', action);
      if (tenant) q.append('tenant_id', tenant);
      const [{ data: s }, { data: r }] = await Promise.all([
        api.get('/gov/audit/cross-tenant/summary?days=30'),
        api.get(`/gov/audit/cross-tenant?${q.toString()}`),
      ]);
      queueMicrotask(() => {
        setSummary(s);
        setRows(r.rows || []);
      });
    } catch (e) {
      queueMicrotask(() => toast.error(e?.response?.data?.detail || 'Could not load audit data.'));
    } finally {
      queueMicrotask(() => setLoading(false));
    }
  };

  useEffect(() => { load(); }, []);


  return (
    <div className="space-y-6" data-testid="gov-audit-page">
      <div className="ve-card p-6 flex items-start gap-4">
        <div className="h-12 w-12 rounded-md flex items-center justify-center shrink-0"
             style={{ background: 'rgba(174,82,234,0.15)', color: 'var(--ve-violet-bright)' }}>
          <ShieldCheck size={22}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="ve-eyebrow">Government · Cross-Tenant Audit</div>
          <h1 className="display text-2xl mt-1">Regulator-grade super-view.</h1>
          <p className="text-sm opacity-70 mt-1 max-w-3xl">
            Every action, every tenant, every actor — read-only and redacted. Personal emails are hashed; free-text labels pass through the universal PII redactor. Each read of this page is itself entered into the audit log.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="gov-audit-refresh"
          className="ve-btn-secondary inline-flex items-center gap-1.5 self-start">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''}/> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* ── Summary tiles ── */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="ve-card p-5" data-testid="gov-audit-total">
            <div className="ve-eyebrow">Total events</div>
            <div className="display text-4xl mt-2 tabular-nums">{summary.total_events?.toLocaleString() ?? '—'}</div>
            <div className="text-[11px] opacity-65 mt-1">over the last {summary.window_days} days</div>
          </div>
          <div className="ve-card p-5">
            <div className="ve-eyebrow">Top tenants</div>
            <div className="mt-3 space-y-1.5 text-sm">
              {(summary.by_tenant || []).slice(0, 5).map((t) => (
                <div key={t.tenant_id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{t.tenant_name}</span>
                  <span className="pal-mono text-[var(--ve-violet-bright)]">{t.events.toLocaleString()}</span>
                </div>
              ))}
              {(summary.by_tenant || []).length === 0 && (
                <div className="text-[11px] opacity-50">No events in window.</div>
              )}
            </div>
          </div>
          <div className="ve-card p-5">
            <div className="ve-eyebrow">Top actions</div>
            <div className="mt-3 space-y-1.5 text-[12px]">
              {(summary.by_action || []).slice(0, 5).map((a) => (
                <div key={a.action} className="flex items-center justify-between gap-2">
                  <span className="truncate pal-mono opacity-85">{a.action}</span>
                  <span className="pal-mono text-emerald-300">{a.events.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Filters + table ── */}
      <div className="ve-card p-6">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-end gap-3 flex-wrap">
            <Filter size={16} className="opacity-60 mb-1.5"/>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] opacity-65">Window</label>
              <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} data-testid="gov-audit-days"
                className="block w-32 mt-1 rounded border border-white/15 bg-transparent px-2 py-1.5 text-[12px] pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]">
                <option value={1}>1 day</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] opacity-65">Action</label>
              <input value={action} onChange={(e) => setAction(e.target.value)} placeholder="e.g. document_uploaded"
                data-testid="gov-audit-action-filter"
                className="block w-56 mt-1 rounded border border-white/15 bg-transparent px-2 py-1.5 text-[12px] pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] opacity-65">Tenant ID</label>
              <input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="(all)"
                data-testid="gov-audit-tenant-filter"
                className="block w-44 mt-1 rounded border border-white/15 bg-transparent px-2 py-1.5 text-[12px] pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]"/>
            </div>
            <button onClick={load} disabled={loading} data-testid="gov-audit-apply-filter"
              className="ve-btn-primary text-[10px] inline-flex items-center gap-1.5">Apply</button>
          </div>
          <button onClick={() => exportCsv(rows)} data-testid="gov-audit-download-csv"
            className="ve-btn-secondary text-[10px] inline-flex items-center gap-1.5">
            <Download size={12}/> CSV
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase tracking-[0.18em] opacity-60 text-left">
              <tr>
                <th className="py-2 pe-3">At (UTC)</th>
                <th className="py-2 pe-3">Tenant</th>
                <th className="py-2 pe-3">Actor</th>
                <th className="py-2 pe-3">Role</th>
                <th className="py-2 pe-3">Action</th>
                <th className="py-2 pe-3">Resource</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan="6" className="py-6 text-center opacity-50">No matching events.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} data-testid={`gov-audit-row-${i}`}
                  className="border-t border-white/5 hover:bg-white/[0.03]">
                  <td className="py-2 pe-3 pal-mono opacity-85 whitespace-nowrap">{fmtAt(r.at)}</td>
                  <td className="py-2 pe-3 truncate max-w-[180px]" title={r.tenant_id}>{r.tenant_name}</td>
                  <td className="py-2 pe-3 pal-mono text-[11px] opacity-75">{r.actor || '(system)'}</td>
                  <td className="py-2 pe-3 pal-mono text-[11px] opacity-75">{r.actor_role || '—'}</td>
                  <td className="py-2 pe-3 pal-mono text-[11px] text-[var(--ve-violet-bright)]">{r.action || '—'}</td>
                  <td className="py-2 pe-3 truncate max-w-[300px]" title={r.resource_label}>
                    <span className="opacity-65 text-[10px] uppercase tracking-[0.12em] me-2">{r.resource_type}</span>
                    {r.resource_label}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
